// Global awareness of in-flight Operations.
//
// Every long-running action in this app (publish, render, fleet install,
// image roll) returns an operationId and settles minutes later. Before
// this hook the only way to learn the outcome was to sit on
// /operations/:id — navigating away made the result silent.
//
// Deliberately split in two:
//   useActiveOperations()          plain query; safe to call from any
//                                  number of components (TanStack dedupes
//                                  on the shared key).
//   useOperationCompletionToasts() the diffing side effect. Mount exactly
//                                  once (Layout) so a completion announces
//                                  once, not once per subscriber.

import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import type { TimelineStep } from "@/components/operation-step-timeline";
import { api, unwrap } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

export const ACTIVE_OPERATIONS_KEY = ["operations", "active"] as const;

/**
 * Fast enough to feel live, slow enough to disappear next to the 10s
 * fleet poll. The payload is at most 20 rows.
 */
const ACTIVE_POLL_MS = 3_000;

/** Failures stay up long enough to be read after a context switch. */
const FAILURE_TOAST_MS = 12_000;

export interface ActiveOperation {
	id: string;
	kind: string;
	status: string;
	startedAt: string;
	finishedAt: string | null;
	exitCode: number | null;
	progress: TimelineStep[] | null;
}

interface OperationsListEnvelope {
	data: ActiveOperation[];
}

interface OperationDetailEnvelope {
	data: ActiveOperation;
}

/**
 * Every operation currently in `running` state, newest first. Shared key,
 * so the dock and the header badge cost one request between them.
 */
export function useActiveOperations() {
	const { user } = useAuth();
	return useQuery({
		queryKey: ACTIVE_OPERATIONS_KEY,
		queryFn: async () => {
			const res = (await unwrap(
				api.api.operations.get({
					query: { status: "running", pageSize: 20 },
				}),
			)) as OperationsListEnvelope;
			return res.data;
		},
		enabled: !!user,
		refetchInterval: ACTIVE_POLL_MS,
		// Being current is this query's entire job — never serve it stale.
		staleTime: 0,
		retry: false,
	});
}

/**
 * Announce operations that left the running set since the last poll.
 *
 * Call once, from the app shell. Bookkeeping lives in refs because it runs
 * on every 3s poll and must never be the reason a render happens.
 */
export function useOperationCompletionToasts(): void {
	const { data } = useActiveOperations();
	const [, navigate] = useLocation();
	const queryClient = useQueryClient();

	const seenRef = useRef<Set<string> | null>(null);
	const navigateRef = useRef(navigate);
	navigateRef.current = navigate;
	const queryClientRef = useRef(queryClient);
	queryClientRef.current = queryClient;

	useEffect(() => {
		if (!data) return;
		const current = new Set(data.map((op) => op.id));
		const previous = seenRef.current;
		seenRef.current = current;
		// The first successful poll only establishes a baseline. Without
		// this guard, opening the app would replay a toast for everything
		// that finished while the tab was closed.
		if (previous === null) return;
		for (const id of previous) {
			if (!current.has(id)) {
				void announce(id, navigateRef, queryClientRef);
			}
		}
	}, [data]);
}

async function announce(
	id: string,
	navigateRef: { current: (to: string) => void },
	queryClientRef: { current: QueryClient },
): Promise<void> {
	let op: ActiveOperation;
	try {
		const res = (await unwrap(
			api.api.operations({ id }).get(),
		)) as OperationDetailEnvelope;
		op = res.data;
	} catch {
		// Row vanished or the session lapsed — nothing useful to say.
		return;
	}

	const affected = affectedQueryRoots(op.kind);
	queryClientRef.current.invalidateQueries({
		predicate: (query) => affected.has(String(query.queryKey[0])),
	});

	const label = describeKind(op.kind);
	const action = {
		label: "View",
		onClick: () => navigateRef.current(`/operations/${op.id}`),
	};

	if (op.status === "succeeded") {
		toast.success(`${label} finished`, { action });
		return;
	}
	// Exit 130 is a cooperative cancel, 137 an orphan reaped after a
	// restart. Neither is a pipeline failure and neither should be red.
	if (op.status === "cancelled" || op.exitCode === 130) {
		toast.warning(`${label} cancelled`, { action });
		return;
	}
	toast.error(`${label} failed`, {
		description: failureHint(op),
		action,
		duration: FAILURE_TOAST_MS,
	});
}

// ---------------------------------------------------------------------------
// What a finished operation actually changed
// ---------------------------------------------------------------------------
//
// This used to invalidate every query in the cache except `auth`, on the
// grounds that one broad refresh is easier to reason about than a per-kind
// map. It is, until you count what a broad refresh costs on this app: `workers`
// and `fleet-sessions` each fan out to every worker in the fleet, so finishing
// any operation fired a full round of worker traffic from every open tab at the
// same instant. Two keys made it worse than wasteful: `view-ticket` mints a
// fresh viewer credential on refetch, and the old screenshot timer refetched a
// CDP capture per visible session.
//
// So the map exists, but it is a map of ROOTS with a deliberately broad
// fallback: an unrecognised kind still refreshes everything live. Adding an
// operation kind and forgetting this file costs a little extra traffic, never a
// stale screen.

/** Refreshed after every operation: the dock and the operations list. */
const ALWAYS: readonly string[] = ["operations", "operation"];

/**
 * Worker reachability, pool membership and the cluster dirty flag.
 *
 * `workers` covers the inventory list AND each worker's history, which share the
 * root now that there is one endpoint behind them. It used to be three roots
 * (`fleet`, `fleet-host`, plus the pool's `cluster.fleet`) over the same rows,
 * and a lifecycle action that refreshed one left the others stale.
 */
const FLEET_ROOTS: readonly string[] = ["cluster", "workers", "provision-info"];

/** Published artifacts and the manifest workers read them through. */
const RELEASE_ROOTS: readonly string[] = [
	"manifest",
	"profile-releases",
	"profile-release",
	"chromium-builds",
	"chromium-upstream",
	"bundles",
];

/**
 * Everything that can go stale, for kinds with no explicit entry. Note what is
 * NOT here: `auth` (a background refresh must never bounce the session),
 * `view-ticket` (refetching mints a new credential), and `system`/`users`,
 * which no operation touches.
 */
const FALLBACK_ROOTS: readonly string[] = [
	...FLEET_ROOTS,
	...RELEASE_ROOTS,
	"fleet-sessions",
	"updates",
	"worker-images",
	"tls-config",
];

const KIND_ROOTS: Record<string, readonly string[]> = {
	// Pool membership and worker lifecycle.
	"provision-local-worker": [...FLEET_ROOTS, "worker-images", "updates"],
	"provision-remote-worker": [...FLEET_ROOTS, "worker-images", "updates"],
	"worker-start": [...FLEET_ROOTS, "fleet-sessions"],
	"worker-stop": [...FLEET_ROOTS, "fleet-sessions"],
	"update-worker": [...FLEET_ROOTS, "updates", "worker-images"],
	"render-config": ["cluster"],
	"update-self": ["updates", "system"],

	// Worker-side installs: what a worker has on disk, against the manifest.
	"fleet-run-update": [...FLEET_ROOTS, "manifest", "updates"],
	chromium: [...FLEET_ROOTS, "manifest"],
	profiles: [...FLEET_ROOTS, "manifest"],

	// Sessions only. Emphatically not the release or manifest queries: a launch
	// used to refetch the whole console.
	"fleet-session-launch": ["fleet-sessions", "workers"],
	"fleet-session-stop": ["fleet-sessions", "workers"],

	// Publishing.
	"publish-chromium": [...RELEASE_ROOTS],
	"publish-profiles": [...RELEASE_ROOTS],
	"activate-chromium-build": [...RELEASE_ROOTS, "workers"],
	"regenerate-manifest": ["manifest"],
	"chromium-catalog-poll": ["chromium-upstream"],
	"chromium-catalog-poll-failed": ["chromium-upstream"],

	// Certificates.
	"acme-warmup": ["cluster", "tls-config"],
	"origin-ca-issue": ["cluster", "tls-config"],
};

/**
 * Query-key roots to invalidate when an operation of this kind finishes.
 * Exported for tests; the map above is the interesting part.
 */
export function affectedQueryRoots(kind: string): Set<string> {
	return new Set([...ALWAYS, ...(KIND_ROOTS[kind] ?? FALLBACK_ROOTS)]);
}

function failureHint(op: ActiveOperation): string | undefined {
	if (op.exitCode === 137) return "Interrupted by a server restart.";
	const failed = op.progress?.find((step) => step.status === "failed");
	if (failed?.error) return `${failed.label}: ${failed.error}`;
	if (failed) return `Failed at "${failed.label}".`;
	return undefined;
}

/** `publish-chromium` reads better as `Publish chromium` in a toast. */
export function describeKind(kind: string): string {
	const spaced = kind.replaceAll("-", " ");
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The step a pipeline is on right now, for the dock's subtext. Steps are
 * appended as they start rather than declared upfront, so there is no
 * honest total to render a determinate bar against.
 */
export function currentStepLabel(op: ActiveOperation): string | null {
	const steps = op.progress;
	if (!steps || steps.length === 0) return null;
	for (let i = steps.length - 1; i >= 0; i--) {
		const step = steps[i];
		if (step?.status === "running") return step.label;
	}
	return steps[steps.length - 1]?.label ?? null;
}

/** Cooperative cancel; falls back to reaping the row when orphaned. */
export function useCancelOperation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			unwrap(api.api.operations({ id }).cancel.post()),
		onSuccess: () => {
			toast.info("Cancellation requested");
			queryClient.invalidateQueries({ queryKey: ACTIVE_OPERATIONS_KEY });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Cancel failed"),
	});
}
